
import React from 'react';
import { Ticket, TicketStatus, FilterType, RepairStageStatus, AppSettings, CommunicationType, Employee, RepairChecklist, AppointmentModalData, InventoryItem } from '../types';
import { TicketList } from './TicketList';

interface AllTicketsPageProps {
  tickets: Ticket[];
  allStatuses: FilterType[];
  activeStatusFilter: FilterType;
  onStatusFilterChange: (filter: FilterType) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile?: (customerName: string) => void;
  onOpenPaymentModal: (ticketId: string, amountDue: number, customerName: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  onShowCustomerPortal?: (ticketId: string) => void;
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void;
  onUpdateRepairStage: (ticketId: string, stageId: string, newStatus: RepairStageStatus, notes?: string) => void;
  onAddCommunicationLog: (ticketId: string, type: CommunicationType, content: string, recipient?: string, staffName?: string) => void;
  onOpenAppointmentModal: (data?: AppointmentModalData) => void;
  appSettings: AppSettings;
  specialFilterActive: boolean;
  currentUser: Employee | null;
  inventoryItems: InventoryItem[];
  onAddPartToTicket: (ticketId: string, inventoryItemId: string, quantityUsed: number, addPriceToTicketTotal: boolean) => void;
  repairChecklists: RepairChecklist[];
  onUpdateTicketChecklistProgress: (ticketId: string, checklistItemId: string, completed: boolean) => void;
}

const TicketIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
    <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
  </svg>
);

const InformationCircleIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-2 text-sky-600" {...props}>
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
  </svg>
);


export const AllTicketsPage: React.FC<AllTicketsPageProps> = ({
  tickets,
  allStatuses,
  activeStatusFilter,
  onStatusFilterChange,
  searchTerm,
  onSearchTermChange,
  onOpenUpdateStatusModal,
  onViewCustomerProfile,
  onOpenPaymentModal,
  onOpenPrintModal,
  onShowCustomerPortal,
  onAddInternalTicketNote,
  onUpdateRepairStage,
  onAddCommunicationLog,
  onOpenAppointmentModal,
  appSettings,
  specialFilterActive,
  currentUser,
  inventoryItems,
  onAddPartToTicket,
  repairChecklists,
  onUpdateTicketChecklistProgress,
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
            <TicketIconSolid />
            <h1 className="text-2xl font-bold text-[var(--color-primary)]">Service Queue</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <input
            type="text"
            placeholder="Search tickets (ID, customer, subject...)"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            className="w-full md:col-span-2 px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"
            aria-label="Search tickets"
          />
          <select
            value={activeStatusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as FilterType)}
            className="w-full px-4 py-2.5 border border-slate-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"
            aria-label="Filter tickets by status"
          >
            {allStatuses.map(status => (
              <option key={status} value={status}>
                {status === 'ALL' ? 'All Statuses' : status}
              </option>
            ))}
          </select>
        </div>
        {specialFilterActive && (
            <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded-md text-sm text-sky-700 flex items-center">
                <InformationCircleIconSolid />
                Showing tickets that are Ready for Pickup or Completed but remain Unpaid.
            </div>
        )}
      </div>
      
      <TicketList
        tickets={tickets}
        onOpenUpdateStatusModal={onOpenUpdateStatusModal}
        onViewCustomerProfile={onViewCustomerProfile}
        onOpenPaymentModal={onOpenPaymentModal}
        onOpenPrintModal={onOpenPrintModal}
        onShowCustomerPortal={onShowCustomerPortal}
        onAddInternalTicketNote={onAddInternalTicketNote}
        onUpdateRepairStage={onUpdateRepairStage}
        onAddCommunicationLog={onAddCommunicationLog}
        onOpenAppointmentModal={onOpenAppointmentModal}
        appSettings={appSettings}
        currentUser={currentUser}
        inventoryItems={inventoryItems}
        onAddPartToTicket={onAddPartToTicket}
        repairChecklists={repairChecklists}
        onUpdateTicketChecklistProgress={onUpdateTicketChecklistProgress}
      />
    </div>
  );
};
